import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { t } from '../utils/i18n';
import { getPriceFromMatrix, filterActiveAddons } from '../services/productApi';
import { useFormStore } from '../state/FormStore';
import { countLedSpots } from '../utils/ledUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost/api';

/**
 * Computes a vendor's total price for the current configuration
 * using the same matrix-based logic as NewVerandaCalculator.
 */
const computeVendorPrice = (vendor, formData) => {
    const width = parseFloat(formData?.maat?.breedte) || 0;
    const depth = parseFloat(formData?.maat?.uitval) || 0;

    if (!vendor.pricing_data || width === 0 || depth === 0) return 0;

    // Normalise: support both 'depths' (legacy API) and 'heights' (CRM editor format)
    const normalisedPricingData = {
        ...vendor.pricing_data,
        depths: vendor.pricing_data.depths || vendor.pricing_data.heights,
    };

    let total = getPriceFromMatrix(normalisedPricingData, width, depth);

    // Add addon prices (same logic as NewVerandaCalculator)
    const activeAddons = filterActiveAddons(vendor.addons || []);

    // Addon type → form field mapping (mirrors NewVerandaCalculator.calculateSelectedAddonsPrices)
    const getAddonByType = (type) => activeAddons.find(a => a.type === type);

    const addAddon = (addon) => {
        if (!addon?.pricing_data) return;
        const pd = {
            ...addon.pricing_data,
            depths: addon.pricing_data.depths || addon.pricing_data.heights,
        };
        total += getPriceFromMatrix(pd, width, depth);
    };

    // Front door
    // Note: 'Glazen Schuifdeur' is NOT here — it is a Railssysteem schuifwand
    // (Sliding Wall) and is priced by the Sliding Wall (Front) block below.
    if (formData?.voorkant?.schuifdeur) {
        const typeMap = {
            'Aluminium Schuifdeur': 'Aluminium Door (Front)',
        };
        const addonType = typeMap[formData.voorkant.schuifdeur];
        if (addonType) addAddon(getAddonByType(addonType));
    }

    // Sides
    const sideTypeMap = {
        'Aluminium Schuifdeur': 'Aluminium Door (Sides)',
        'Glazen Schuifdeur': 'Glass Door (Sides)',
    };
    if (formData?.linker?.linker_wand_option && formData.linker.linker_wand_option !== 'Geen') {
        addAddon(getAddonByType(sideTypeMap[formData.linker.linker_wand_option]));
    }
    if (formData?.rechter?.rechter_wand_option && formData.rechter.rechter_wand_option !== 'Geen') {
        addAddon(getAddonByType(sideTypeMap[formData.rechter.rechter_wand_option]));
    }

    // Zonwering
    if (formData?.zonwering?.zonwering_option === 'Ja') {
        addAddon(getAddonByType('Zonwering'));
    }

    // Gutter
    if (formData?.gutter?.goot_option && formData.gutter.goot_option !== 'Geen') {
        addAddon(getAddonByType('Gutter'));
    }

    // Spie (left/right) — look up by selected name to get correct price matrix
    if (formData?.linker?.linker_spie_option && formData.linker.linker_spie_option !== 'Geen') {
        const spie = activeAddons.find(a => a.type === 'Spie' && a.name === formData.linker.linker_spie_option);
        if (spie?.pricing_data) {
            const pd = { ...spie.pricing_data, depths: spie.pricing_data.depths || spie.pricing_data.heights };
            total += getPriceFromMatrix(pd, width, depth);
        }
    }
    if (formData?.rechter?.rechter_spie_option && formData.rechter.rechter_spie_option !== 'Geen') {
        const spie = activeAddons.find(a => a.type === 'Spie' && a.name === formData.rechter.rechter_spie_option);
        if (spie?.pricing_data) {
            const pd = { ...spie.pricing_data, depths: spie.pricing_data.depths || spie.pricing_data.heights };
            total += getPriceFromMatrix(pd, width, depth);
        }
    }

    // Roof addon by name
    if (formData?.dak?.dak_option) {
        const roofAddon = activeAddons.find(a => a.name === formData.dak.dak_option);
        if (roofAddon?.pricing_data) {
            const pd = {
                ...roofAddon.pricing_data,
                depths: roofAddon.pricing_data.depths || roofAddon.pricing_data.heights,
            };
            total += getPriceFromMatrix(pd, width, depth);
        }
    }

    // Sliding Wall (Front) — Railssysteem schuifwanden pricing
    // Triggered when user selects 'Glazen Schuifdeur' as voorkant.
    // Rail count is encoded in the addon matrix width axis:
    //   width 300 → 3 rails, 400 → 4 rails, 500 → 5 rails, 600 → 6 rails
    // Glass type is explicitly selected via the right-side sub-options panel.
    if (formData?.voorkant?.schuifdeur === 'Glazen Schuifdeur') {
        const isGetint = formData?.voorkant_glazen_schuifdeur_options?.glazen_schuifdeur_type === 'Getint Glas';
        const swName = isGetint ? 'Getint Glas' : 'Helder Glas';
        const swAddon = activeAddons.find(
            a => a.type === 'Sliding Wall (Front)' && a.name === swName
        );
        if (swAddon?.pricing_data) {
            const pd = {
                ...swAddon.pricing_data,
                depths: swAddon.pricing_data.depths || swAddon.pricing_data.heights,
            };
            total += getPriceFromMatrix(pd, width, depth);
        }
    }

    // LED Verlichting — per-piece pricing: price_per_spot × total_spots
    if (formData?.verlichting?.led_option === 'Ja') {
        const selectedPattern = formData?.verlichting?.led_patroon_option;
        if (selectedPattern && selectedPattern !== 'Geen') {
            const ledAddon = activeAddons.find(
                a => a.type === 'LED' && a.name === selectedPattern
            );
            if (ledAddon?.pricing_data) {
                const pd = {
                    ...ledAddon.pricing_data,
                    depths: ledAddon.pricing_data.depths || ledAddon.pricing_data.heights,
                };
                const pricePerPiece = getPriceFromMatrix(pd, width, depth);
                const totalSpots = countLedSpots(width, selectedPattern);
                total += pricePerPiece * totalSpots;
            }
        }
    }

    return Math.round(total);
};

/** Initials avatar as fallback for company logo */
const InitialsAvatar = ({ name, size = 32 }) => {
    const initials = name
        .split(' ')
        .slice(0, 2)
        .map(w => w[0] || '')
        .join('')
        .toUpperCase();

    const colors = [
        ['#2196f3', '#1565c0'],
        ['#4caf50', '#2e7d32'],
        ['#ff9800', '#e65100'],
        ['#9c27b0', '#6a1b9a'],
        ['#f44336', '#b71c1c'],
        ['#00bcd4', '#006064'],
    ];
    const colorPair = colors[name.charCodeAt(0) % colors.length];

    return (
        <div style={{
            width: size,
            height: size,
            borderRadius: '6px',
            background: `linear-gradient(135deg, ${colorPair[0]}, ${colorPair[1]})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: size * 0.38,
            fontWeight: '700',
            flexShrink: 0,
            letterSpacing: '0.5px',
        }}>
            {initials}
        </div>
    );
};

/** Single skeleton card shown while loading */
const SkeletonCard = () => (
    <div style={{
        background: 'white',
        borderRadius: 6,
        padding: '10px',
        minWidth: 170,
        border: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    }}>
        {[100, 70, 50, 80, 30].map((w, i) => (
            <div key={i} style={{
                height: i === 0 ? 12 : 8,
                width: `${w}%`,
                background: '#f3f4f6',
                borderRadius: 4,
                animation: 'pulse 1.5s ease-in-out infinite',
            }} />
        ))}
        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>
    </div>
);

const VendorPrices = () => {
    const { formData } = useFormStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Map purchase_mode option → API type param
    const modeOption = formData?.purchase_mode?.mode_option || 'Complete Veranda';
    const isStandalone = modeOption === 'Standalone Doors Only';

    // Currently selected product_id from URL (reactive via React Router)
    const currentProductId = searchParams.get('product_id');

    // Helper: update product_id in URL without page reload
    const selectVendor = (id) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('product_id', String(id));
            return next;
        });
    };

    // Re-fetch vendors whenever the purchase mode changes
    useEffect(() => {
        setLoading(true);
        setError(null);
        setVendors([]);

        const fetchVendors = async () => {
            try {
                // For standalone doors, fetch both aluminium_door and glass_door vendors
                const typeParam = isStandalone
                    ? 'type[]=aluminium_door&type[]=glass_door'
                    : 'type=veranda';

                const res = await fetch(`${API_BASE_URL}/public/configurator/vendors?${typeParam}`, {
                    headers: { 'Accept': 'application/json' },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                const loaded = json.data || [];
                setVendors(loaded);

                // Auto-select the first vendor if the current product_id
                // doesn't belong to this filtered set
                const ids = loaded.map(v => String(v.id));
                const pid = new URLSearchParams(window.location.search).get('product_id');
                if (loaded.length > 0 && (!pid || !ids.includes(pid))) {
                    selectVendor(loaded[0].id);
                }
            } catch (err) {
                console.error('VendorPrices: failed to load vendors', err);
                setError('Could not load vendor prices');
            } finally {
                setLoading(false);
            }
        };

        fetchVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStandalone]); // re-fetch when purchase mode changes

    // Compute prices for every vendor whenever formData changes
    const vendorCards = vendors.map(vendor => ({
        ...vendor,
        computedPrice: computeVendorPrice(vendor, formData),
    })).sort((a, b) => a.computedPrice - b.computedPrice);


    return (
        <div className="vendor-prices-section border-t border-gray-300" style={{ background: '#f9fafb' }}>
            <div className="overflow-x-auto no-scrollbar" style={{ padding: '6px 8px' }}>
                <div className="flex gap-2" style={{ minWidth: 'max-content' }}>

                    {loading && [1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}

                    {!loading && error && (
                        <div style={{ padding: '4px 8px', color: '#6b7280', fontSize: 11 }}>
                            {error}
                        </div>
                    )}

                    {!loading && !error && vendorCards.length === 0 && (
                        <div style={{ padding: '4px 8px', color: '#6b7280', fontSize: 11 }}>
                            {t('No vendors available')}
                        </div>
                    )}

                    {!loading && vendorCards.map((vendor, index) => {
                        const isSelected = String(vendor.id) === String(currentProductId);
                        const isLowest = index === 0 && vendor.computedPrice > 0;
                        
                        return (
                            <div
                                key={vendor.id}
                                onClick={() => { if (!isSelected) selectVendor(vendor.id); }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '6px 10px',
                                    minWidth: 160,
                                    background: isSelected ? '#eef2ff' : '#fff',
                                    border: isSelected ? '1.5px solid #4f46e5' : '1px solid #e5e7eb',
                                    borderRadius: 6,
                                    cursor: isSelected ? 'default' : 'pointer',
                                    transition: 'all 0.15s ease',
                                }}
                            >
                                {/* Avatar */}
                                <div style={{ flexShrink: 0 }}>
                                    {vendor.logo
                                        ? <img src={vendor.logo} alt={vendor.name} style={{ width: 26, height: 26, borderRadius: 4, objectFit: 'cover' }} />
                                        : <InitialsAvatar name={vendor.name} size={26} />
                                    }
                                </div>

                                {/* Info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 10, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {vendor.name}
                                    </div>
                                    {vendor.product_name && (
                                        <div style={{ fontSize: 9, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {vendor.product_name}
                                        </div>
                                    )}
                                    {vendor.lead_time_days && (
                                        <div style={{ fontSize: 9, color: '#9ca3af' }}>
                                            {vendor.lead_time_days} {t('days')}
                                        </div>
                                    )}
                                </div>

                                {/* Price + status */}
                                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                    {vendor.computedPrice > 0 ? (
                                        <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? '#4f46e5' : (isLowest ? '#16a34a' : '#1f2937') }}>
                                            €{vendor.computedPrice.toLocaleString()}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: 9, color: '#9ca3af', fontStyle: 'italic' }}>—</div>
                                    )}
                                    <div style={{ fontSize: 8, fontWeight: 600, marginTop: 1, color: isSelected ? '#4f46e5' : (isLowest ? '#16a34a' : 'transparent') }}>
                                        {isSelected ? '● ACTIVE' : (isLowest ? 'BEST' : '.')}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default VendorPrices;

